import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header, BottomNav } from "../components/Header";
import { Card, CardContent } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Users, Search, Phone, CreditCard, ShoppingCart, Gavel, Tag, MessageCircle, Edit2, Ban, CheckCircle, Trash2, Bug, ChevronDown, ChevronUp, KeyRound, Smartphone } from "lucide-react";

const ClientPanelPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Edit modal state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", document_type: "cedula", document_number: "", phone_number: "" });
  const [saving, setSaving] = useState(false);
  
  // Delete/Block confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  // Debug section state
  const [debugExpanded, setDebugExpanded] = useState(false);
  const [debugSearchTerm, setDebugSearchTerm] = useState("");
  const [debugResults, setDebugResults] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [allUsersData, setAllUsersData] = useState(null);

  useEffect(() => {
    fetchClients();
  }, [token]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setLoading(false);
    }
  };

  // Debug functions
  const searchUserByName = async () => {
    if (!debugSearchTerm.trim()) {
      toast.error("Ingresa un nombre para buscar");
      return;
    }
    setDebugLoading(true);
    try {
      const response = await axios.get(`${API}/admin/debug/search-user/${encodeURIComponent(debugSearchTerm)}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setDebugResults(response.data);
    } catch (error) {
      console.error("Error searching user:", error);
      toast.error("Error al buscar usuario");
    } finally {
      setDebugLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    setDebugLoading(true);
    try {
      const response = await axios.get(`${API}/admin/debug/all-users`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setAllUsersData(response.data);
    } catch (error) {
      console.error("Error fetching all users:", error);
      toast.error("Error al cargar usuarios");
    } finally {
      setDebugLoading(false);
    }
  };

  const filteredClients = clients.filter(
    (client) =>
      client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.document_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone_number?.includes(searchTerm)
  );

  const openEditDialog = (client) => {
    setSelectedClient(client);
    setEditForm({
      name: client.name || "",
      document_type: client.document_type || "cedula",
      document_number: client.document_number || "",
      phone_number: client.phone_number || "+507 "
    });
    setEditDialogOpen(true);
  };

  const handleSaveClient = async () => {
    if (!selectedClient) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/clients/${selectedClient.user_id}`,
        editForm,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success("Cliente actualizado exitosamente");
      setEditDialogOpen(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar cliente");
    } finally {
      setSaving(false);
    }
  };

  const handleBlockClient = async () => {
    if (!clientToDelete) return;
    setSaving(true);
    try {
      await axios.delete(
        `${API}/admin/clients/${clientToDelete.user_id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success("Cliente bloqueado exitosamente");
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al bloquear cliente");
    } finally {
      setSaving(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!clientToDelete) return;
    setSaving(true);
    try {
      const response = await axios.delete(
        `${API}/admin/clients/${clientToDelete.user_id}/permanent`,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success(response.data.message);
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      fetchClients();
      // Also refresh debug data if expanded
      if (debugExpanded && allUsersData) {
        fetchAllUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar cliente");
    } finally {
      setSaving(false);
    }
  };

  const handleUnblockClient = async (clientId) => {
    try {
      await axios.post(
        `${API}/admin/clients/${clientId}/unblock`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success("Cliente desbloqueado");
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al desbloquear cliente");
    }
  };

  const handleResetWhatsAppPassword = async (client) => {
    if (!client.whatsapp) {
      toast.error("Este cliente no tiene WhatsApp registrado");
      return;
    }
    
    if (!window.confirm(`¿Resetear la contraseña de ${client.name || client.username}?\n\nEsto permitirá que el usuario ingrese con su WhatsApp (${client.whatsapp}) como contraseña.`)) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/clients/${client.user_id}/reset-whatsapp-password`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al resetear contraseña");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8" data-testid="client-panel-page">
      <Header />

      <main className="max-w-4xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-6"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
          Volver
        </button>

        <div className="flex items-center gap-3 mb-6">
          <Users className="h-8 w-8 text-[#475569]" />
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A]">Panel de Clientes</h1>
            <p className="text-sm text-[#64748B]">
              Compradores que han participado en tus productos
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#64748B]" />
          <Input
            placeholder="Buscar por nombre, email, cédula o WhatsApp..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 rounded-full border-[#E2E8F0] h-12"
            data-testid="client-search"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-white border-[#E2E8F0]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#0F172A]">{clients.length}</p>
              <p className="text-sm text-[#64748B]">Total Clientes</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E2E8F0]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">
                {clients.filter((c) => c.profile_completed).length}
              </p>
              <p className="text-sm text-[#64748B]">Perfiles Completos</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E2E8F0]">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">
                {clients.filter((c) => c.purchase_count > 0).length}
              </p>
              <p className="text-sm text-[#64748B]">Con Compras</p>
            </CardContent>
          </Card>
        </div>

        {/* Debug Section - Collapsible */}
        <Card className="bg-amber-50 border-amber-200 mb-6">
          <CardContent className="p-4">
            <button 
              onClick={() => setDebugExpanded(!debugExpanded)}
              className="flex items-center justify-between w-full text-left"
              data-testid="debug-toggle"
            >
              <div className="flex items-center gap-2">
                <Bug className="h-5 w-5 text-amber-600" />
                <span className="font-semibold text-amber-800">Debug - Buscar Usuarios en BD</span>
              </div>
              {debugExpanded ? <ChevronUp className="h-5 w-5 text-amber-600" /> : <ChevronDown className="h-5 w-5 text-amber-600" />}
            </button>
            
            {debugExpanded && (
              <div className="mt-4 space-y-4">
                {/* Search by name */}
                <div>
                  <p className="text-sm text-amber-700 mb-2">Buscar usuario por nombre (incluye usuarios sin perfil completo):</p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Ej: Aquilino"
                      value={debugSearchTerm}
                      onChange={(e) => setDebugSearchTerm(e.target.value)}
                      className="flex-1 border-amber-300"
                      data-testid="debug-search-input"
                      onKeyDown={(e) => e.key === 'Enter' && searchUserByName()}
                    />
                    <Button 
                      onClick={searchUserByName} 
                      disabled={debugLoading}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="debug-search-btn"
                    >
                      {debugLoading ? "..." : "Buscar"}
                    </Button>
                  </div>
                </div>

                {/* Search results */}
                {debugResults && (
                  <div className="bg-white rounded-lg p-3 border border-amber-200">
                    <p className="text-sm font-medium text-amber-800 mb-2">{debugResults.message}</p>
                    {debugResults.users?.length > 0 ? (
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {debugResults.users.map((user, idx) => (
                          <div key={idx} className="bg-gray-50 rounded p-3 text-sm">
                            <p><strong>Nombre:</strong> {user.name || "Sin nombre"}</p>
                            <p><strong>Email:</strong> {user.email}</p>
                            <p><strong>Teléfono:</strong> {user.phone_number || "No registrado"}</p>
                            <p><strong>Perfil Completo:</strong> {user.profile_completed ? "Sí" : "No"}</p>
                            <p><strong>ID:</strong> <span className="font-mono text-xs">{user.user_id}</span></p>
                            <p className="mt-1 text-amber-700">
                              <strong>Actividad:</strong> {user.activity?.messages_sent || 0} mensajes, {user.activity?.purchase_intents || 0} solicitudes, {user.activity?.bids || 0} ofertas, {user.activity?.offers || 0} contraofertas
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No se encontraron usuarios.</p>
                    )}
                  </div>
                )}

                {/* Load all users button */}
                <div className="border-t border-amber-200 pt-4">
                  <Button 
                    onClick={fetchAllUsers} 
                    disabled={debugLoading}
                    variant="outline"
                    className="w-full border-amber-400 text-amber-700 hover:bg-amber-100"
                    data-testid="debug-all-users-btn"
                  >
                    {debugLoading ? "Cargando..." : "Ver TODOS los usuarios de la BD"}
                  </Button>
                  
                  {allUsersData && (
                    <div className="mt-3 bg-white rounded-lg p-3 border border-amber-200">
                      <p className="text-sm font-medium text-amber-800 mb-2">Total: {allUsersData.total_users} usuarios en la base de datos</p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {allUsersData.users?.map((user, idx) => (
                          <div key={idx} className="bg-gray-50 rounded p-2 text-xs">
                            <p><strong>{user.name || "Sin nombre"}</strong> - {user.email}</p>
                            <p className="text-gray-500">Perfil: {user.profile_completed ? "Completo" : "Incompleto"} | Tel: {user.phone_number || "N/A"}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Client List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : filteredClients.length === 0 ? (
          <Card className="bg-white border-[#E2E8F0]">
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 text-[#CBD5E1] mx-auto mb-3" />
              <p className="text-[#64748B]">
                {searchTerm ? "No se encontraron clientes" : "Aún no tienes clientes"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredClients.map((client) => (
              <Card
                key={client.user_id}
                className="bg-white border-[#E2E8F0] hover:shadow-md transition-shadow"
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <Avatar className="h-14 w-14">
                      <AvatarImage src={client.picture} alt={client.name} />
                      <AvatarFallback className="bg-[#475569] text-white text-lg">
                        {client.name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-[#0F172A]">{client.name}</h3>
                        {client.profile_completed ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            Verificado
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">
                            Sin verificar
                          </Badge>
                        )}
                        {client.is_blocked && (
                          <Badge className="bg-red-100 text-red-700 text-xs">
                            Bloqueado
                          </Badge>
                        )}
                      </div>

                      <p className="text-sm text-[#64748B] truncate">{client.email}</p>
                      
                      {/* WhatsApp info for WhatsApp users */}
                      {client.whatsapp && (
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className="bg-green-100 text-green-700 text-xs flex items-center gap-1">
                            <Smartphone size={10} />
                            {client.username || "sin usuario"}
                          </Badge>
                          <span className="text-xs text-[#64748B]">WA: {client.whatsapp}</span>
                        </div>
                      )}

                      {client.profile_completed && (
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-sm">
                          <span className="flex items-center gap-1 text-[#64748B]">
                            <CreditCard size={14} />
                            {client.document_type === 'pasaporte' ? 'Pasaporte' : 'Cédula'}: {client.document_number}
                          </span>
                          <a
                            href={`tel:${client.phone_number?.replace(/[^0-9+]/g, "")}`}
                            className="flex items-center gap-1 text-[#475569] hover:underline"
                          >
                            <Phone size={14} />
                            {client.phone_number}
                          </a>
                          <Link
                            to={`/chat/${client.user_id}`}
                            className="flex items-center gap-1 text-[#475569] hover:text-[#334155] hover:underline"
                          >
                            <MessageCircle size={14} />
                            Mensaje
                          </Link>
                        </div>
                      )}
                      
                      {!client.profile_completed && (
                        <div className="mt-2">
                          <Link
                            to={`/chat/${client.user_id}`}
                            className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#334155] hover:underline"
                          >
                            <MessageCircle size={14} />
                            Enviar Mensaje
                          </Link>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className="flex items-center gap-1 text-xs px-2 py-1 bg-[#F1F5F9] rounded-full">
                          <ShoppingCart size={12} className="text-green-600" />
                          {client.purchase_count} compras
                        </span>
                        <span className="flex items-center gap-1 text-xs px-2 py-1 bg-[#F1F5F9] rounded-full">
                          <Tag size={12} className="text-purple-600" />
                          {client.intent_count || 0} solicitudes
                        </span>
                        <span className="flex items-center gap-1 text-xs px-2 py-1 bg-[#F1F5F9] rounded-full">
                          <Gavel size={12} className="text-blue-600" />
                          {client.bid_count} ofertas
                        </span>
                        <span className="flex items-center gap-1 text-xs px-2 py-1 bg-[#F1F5F9] rounded-full">
                          <Tag size={12} className="text-orange-600" />
                          {client.offer_count} ofertas
                        </span>
                        <span className="flex items-center gap-1 text-xs px-2 py-1 bg-[#F1F5F9] rounded-full">
                          <MessageCircle size={12} className="text-gray-600" />
                          {client.message_count || 0} msgs
                        </span>
                      </div>
                      
                      {/* Admin Actions */}
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#E2E8F0]">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(client)}
                          className="text-xs"
                          data-testid={`edit-client-${client.user_id}`}
                        >
                          <Edit2 size={14} className="mr-1" />
                          Editar
                        </Button>
                        {client.whatsapp && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResetWhatsAppPassword(client)}
                            className="text-xs text-blue-600 border-blue-600 hover:bg-blue-50"
                            data-testid={`reset-wa-${client.user_id}`}
                            title="Resetear contraseña WhatsApp"
                          >
                            <KeyRound size={14} className="mr-1" />
                            Reset WA
                          </Button>
                        )}
                        {client.is_blocked ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUnblockClient(client.user_id)}
                            className="text-xs text-green-600 border-green-600 hover:bg-green-50"
                            data-testid={`unblock-client-${client.user_id}`}
                          >
                            <CheckCircle size={14} className="mr-1" />
                            Desbloquear
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setClientToDelete(client); setDeleteDialogOpen(true); }}
                            className="text-xs text-red-600 border-red-600 hover:bg-red-50"
                            data-testid={`block-client-${client.user_id}`}
                          >
                            <Ban size={14} className="mr-1" />
                            Bloquear
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Client Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Editar Cliente</DialogTitle>
              <DialogDescription>
                Actualiza la información del cliente
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Nombre</Label>
                <Input
                  id="edit-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="rounded-xl"
                  data-testid="edit-client-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-document">Documento ({editForm.document_type === 'pasaporte' ? 'Pasaporte' : 'Cédula'})</Label>
                <Input
                  id="edit-document"
                  value={editForm.document_number}
                  onChange={(e) => setEditForm({ ...editForm, document_number: e.target.value })}
                  className="rounded-xl"
                  data-testid="edit-client-document"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Teléfono</Label>
                <Input
                  id="edit-phone"
                  value={editForm.phone_number}
                  onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })}
                  className="rounded-xl"
                  data-testid="edit-client-phone"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="rounded-full">
                Cancelar
              </Button>
              <Button onClick={handleSaveClient} disabled={saving} className="rounded-full bg-[#475569] hover:bg-[#334155]">
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Block Client Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600">Gestionar Cliente</DialogTitle>
              <DialogDescription>
                ¿Qué deseas hacer con <strong>{clientToDelete?.name}</strong>?
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-semibold text-amber-800 mb-1">Bloquear (Temporal)</h4>
                <p className="text-sm text-amber-700 mb-3">
                  El cliente no podrá iniciar sesión ni participar. Puedes desbloquearlo después.
                </p>
                <Button 
                  onClick={handleBlockClient} 
                  disabled={saving} 
                  className="w-full rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Ban size={16} className="mr-2" />
                  {saving ? "Bloqueando..." : "Bloquear Cliente"}
                </Button>
              </div>
              
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h4 className="font-semibold text-red-800 mb-1">Eliminar Permanentemente</h4>
                <p className="text-sm text-red-700 mb-3">
                  ⚠️ Esto eliminará TODOS los datos del cliente: mensajes, ofertas, solicitudes, reviews, etc. Esta acción NO se puede deshacer.
                </p>
                <Button 
                  onClick={handlePermanentDelete} 
                  disabled={saving} 
                  variant="destructive"
                  className="w-full rounded-full"
                >
                  <Trash2 size={16} className="mr-2" />
                  {saving ? "Eliminando..." : "Eliminar Permanentemente"}
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setClientToDelete(null); }} className="rounded-full w-full">
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>

      <BottomNav />
    </div>
  );
};

export default ClientPanelPage;
